import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Heart, LogOut, Download, Search, Loader2, Euro, Grid3x3, ShoppingBag, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API, fileUrl } from "@/lib/api";

const authApi = axios.create({ baseURL: API, withCredentials: true });

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl grid place-items-center ${accent}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
          <p className="text-2xl font-bold tabular-nums text-slate-50">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null); // null=checking, false=not auth, object=auth
  const [notAuthorized, setNotAuthorized] = useState(false);
  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const processed = useRef(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        authApi.get("/admin/stats"),
        authApi.get("/admin/purchases"),
      ]);
      setStats(s.data);
      setPurchases(p.data);
    } catch {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, []);

  const checkMe = useCallback(async () => {
    try {
      const { data } = await authApi.get("/auth/me");
      setUser(data);
      loadData();
    } catch {
      setUser(false);
    }
  }, [loadData]);

  const processSession = useCallback(async (sid) => {
    try {
      const { data } = await authApi.post("/auth/session", { session_id: sid });
      window.history.replaceState(null, "", window.location.pathname);
      setUser(data);
      loadData();
    } catch (e) {
      window.history.replaceState(null, "", window.location.pathname);
      if (e.response?.status === 403) setNotAuthorized(true);
      setUser(false);
    }
  }, [loadData]);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      processSession(sid);
    } else {
      checkMe();
    }
  }, [checkMe, processSession]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await authApi.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  };

  const downloadCsv = async () => {
    try {
      const res = await authApi.get("/admin/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "acquisti_lovewall.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nell'esportazione"); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return purchases;
    const s = q.toLowerCase();
    return purchases.filter((p) =>
      (p.buyer_name || "").toLowerCase().includes(s) ||
      (p.dedicated_to || "").toLowerCase().includes(s) ||
      (p.message || "").toLowerCase().includes(s));
  }, [q, purchases]);

  // ---- checking ----
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0a0e17] grid place-items-center">
        <Loader2 className="animate-spin text-rose-400" size={40} />
      </div>
    );
  }

  // ---- login ----
  if (user === false) {
    return (
      <div className="min-h-screen bg-[#0a0e17] grid place-items-center px-4">
        <div data-testid="admin-login-card" className="max-w-md w-full text-center rounded-2xl border border-rose-900/40 bg-slate-950 p-10">
          <Heart className="mx-auto text-rose-500 fill-rose-500" size={40} />
          <h1 className="font-display text-2xl font-bold mt-4 text-slate-50">Area Amministratore</h1>
          <p className="text-slate-400 mt-2 text-sm">Accedi per vedere gli acquisti del Muro dell'Amore.</p>
          {notAuthorized && (
            <div className="mt-4 flex items-center gap-2 justify-center text-sm text-rose-400 bg-rose-950/30 border border-rose-900/40 rounded-lg px-3 py-2">
              <ShieldAlert size={16} /> Account non autorizzato all'accesso admin.
            </div>
          )}
          <Button data-testid="google-login-button" onClick={login}
            className="mt-6 w-full h-12 bg-white text-slate-900 hover:bg-slate-100 rounded-full font-semibold">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" />
            Accedi con Google
          </Button>
        </div>
      </div>
    );
  }

  // ---- dashboard ----
  return (
    <div className="min-h-screen bg-[#0a0e17] text-slate-100">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-slate-950/80 border-b border-rose-950/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Heart className="text-rose-500 fill-rose-500" size={22} />
            <span className="font-display text-lg font-extrabold">Love Wall · Admin</span>
          </div>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-slate-700" />}
            <span className="hidden sm:block text-sm text-slate-400">{user.email}</span>
            <Button data-testid="admin-logout-button" onClick={logout} variant="secondary" className="bg-slate-800 hover:bg-slate-700 h-9">
              <LogOut size={15} className="mr-1.5" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Euro} label="Totale incassato" value={stats ? `€${stats.total_revenue_eur.toLocaleString("it-IT")}` : "—"} accent="bg-amber-400/15 text-amber-400" />
          <StatCard icon={Grid3x3} label="Pixel venduti" value={stats ? stats.pixels_sold.toLocaleString("it-IT") : "—"} accent="bg-rose-500/15 text-rose-400" />
          <StatCard icon={ShoppingBag} label="Acquisti totali" value={stats ? stats.total_purchases.toLocaleString("it-IT") : "—"} accent="bg-sky-500/15 text-sky-400" />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-xl font-bold text-rose-50">Acquisti ({filtered.length})</h2>
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={15} />
              <Input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca per nome..." className="pl-9 bg-slate-900 border-slate-700 h-10 w-full sm:w-64" />
            </div>
            <Button data-testid="admin-export-button" onClick={downloadCsv} className="bg-emerald-600 hover:bg-emerald-500 h-10">
              <Download size={16} className="mr-1.5" /> CSV
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-purchases-table">
              <thead className="bg-slate-900/80 text-slate-400 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Foto</th>
                  <th className="px-4 py-3 font-medium">Acquirente</th>
                  <th className="px-4 py-3 font-medium">Dedicato a</th>
                  <th className="px-4 py-3 font-medium">Messaggio</th>
                  <th className="px-4 py-3 font-medium text-right">Pixel</th>
                  <th className="px-4 py-3 font-medium text-right">Importo</th>
                  <th className="px-4 py-3 font-medium">Data</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Nessun acquisto trovato</td></tr>
                ) : filtered.map((p) => (
                  <tr key={p.session_id} className="border-t border-slate-800 hover:bg-slate-900/40">
                    <td className="px-4 py-3">
                      {p.photo_path ? (
                        <img src={fileUrl(p.photo_path)} alt="" className="w-10 h-10 rounded-md object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-[#16223f] grid place-items-center"><Heart size={14} className="text-rose-600 fill-rose-600" /></div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-200">{p.buyer_name}</td>
                    <td className="px-4 py-3 text-rose-300">{p.dedicated_to}</td>
                    <td className="px-4 py-3 text-slate-400 max-w-[240px] truncate">{p.message || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{p.pixel_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-400">€{p.amount_eur.toFixed(2)}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(p.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
